'use client'

import { IconInfoCircle } from '@macarongfactory/web-icons'
import { Text } from '@macarongfactory/web-ui'
import { useState } from 'react'

interface IInfoTooltipProps {
  /** 툴팁 본문 */
  text: string
  /** 스크린리더용 버튼 라벨 */
  ariaLabel: string
  /** ⓘ 아이콘 크기(px) */
  iconSize?: number
  /** 툴팁 노출 방향 (기본: 아이콘 위, 아래 화살표) */
  placement?: 'top' | 'bottom'
  /** 툴팁 너비(px) */
  width?: number
  /**
   * 가로 정렬 — 'center'(아이콘 중앙) | 'right'(아이콘 우측 정렬).
   * 아이콘이 프레임 오른쪽에 붙어 툴팁이 잘리는 경우 'right'로 왼쪽으로 펼친다.
   */
  align?: 'center' | 'right'
}

const DEFAULT_ICON_SIZE = 18
const DEFAULT_WIDTH = 200
/** 화살표 한 변(px) — w-2/h-2 = 8px */
const ARROW_SIZE_PX = 8

/**
 * ⓘ 클릭 시 다크 툴팁을 토글하는 인포 버튼 (Figma 공인연비 Tooltip)
 * - 다크 버블(#343e47) + 방향 화살표
 */
const InfoTooltip = ({
  text,
  ariaLabel,
  iconSize = DEFAULT_ICON_SIZE,
  placement = 'top',
  width = DEFAULT_WIDTH,
  align = 'center',
}: IInfoTooltipProps) => {
  const [open, setOpen] = useState(false)
  const isTop = placement === 'top'
  const isRight = align === 'right'

  // 가로 위치 — center: 아이콘 중앙 / right: 아이콘 우측에 맞춰 왼쪽으로 펼침(프레임 잘림 방지)
  const boxAlignClass = isRight ? 'right-0' : 'left-1/2 -translate-x-1/2'
  // 화살표는 항상 아이콘 중앙을 가리킨다. right 정렬 땐 우측에서 아이콘 반경만큼 안쪽.
  const arrowAlignClass = isRight ? '' : 'left-1/2 -translate-x-1/2'
  const arrowAlignStyle = isRight
    ? { right: iconSize / 2 - ARROW_SIZE_PX / 2 }
    : undefined

  return (
    <span className='relative inline-flex'>
      <button
        type='button'
        aria-label={ariaLabel}
        aria-expanded={open}
        onClick={() => setOpen(prev => !prev)}
        className='inline-flex text-foreground-neutral-assistive'
      >
        <IconInfoCircle width={iconSize} height={iconSize} />
      </button>
      {open ? (
        <div
          role='tooltip'
          className={`absolute z-10 ${boxAlignClass} ${
            isTop ? 'bottom-[calc(100%+6px)]' : 'top-[calc(100%+6px)]'
          }`}
          style={{ width }}
        >
          <div className='rounded-dimension-x1 bg-background-neutral-inverse px-dimension-x3 py-dimension-x2'>
            <Text
              variant='body-2-regular'
              color='text-foreground-neutral-inverse'
              className='break-keep text-center'
            >
              {text}
            </Text>
          </div>
          {/* 아이콘을 향하는 화살표 */}
          <span
            aria-hidden
            style={arrowAlignStyle}
            className={`absolute h-2 w-2 rotate-45 bg-background-neutral-inverse ${arrowAlignClass} ${
              isTop
                ? 'top-full -translate-y-1/2'
                : 'bottom-full translate-y-1/2'
            }`}
          />
        </div>
      ) : null}
    </span>
  )
}

export default InfoTooltip
